/**
 * Storage Migration Utility
 * Migrates data from localStorage to IndexedDB
 */
class StorageMigration {
    constructor(sourceStorage, targetStorage) {
        this.sourceStorage = sourceStorage;
        this.targetStorage = targetStorage;
        this.migrationKey = '__storage_migration_completed__';
    }

    /**
     * Check if migration has already been completed
     * @returns {Promise<boolean>}
     */
    async isMigrationCompleted() {
        try {
            const completed = await this.targetStorage.get(this.migrationKey);
            return completed === true;
        } catch (error) {
            console.error('Error checking migration status:', error);
            return false;
        }
    }

    /**
     * Mark migration as completed
     * @private
     */
    async markMigrationCompleted() {
        await this.targetStorage.set(this.migrationKey, true);
    }

    /**
     * Migrate all data from source to target storage
     * @param {boolean} clearSource - Whether to clear source storage after migration
     * @returns {Promise<Object>} Migration result with stats
     */
    async migrate(clearSource = false) {
        console.log('Starting storage migration...');

        const result = {
            success: false,
            itemsMigrated: 0,
            itemsFailed: 0,
            errors: [],
            startTime: Date.now(),
            endTime: null
        };

        try {
            // Check if migration already completed
            if (await this.isMigrationCompleted()) {
                console.log('✅ Migration already completed, skipping');
                result.success = true;
                result.endTime = Date.now();
                return result;
            }

            // Get all keys from source storage
            const keys = await this.sourceStorage.keys();
            console.log(`Found ${keys.length} items to migrate`);

            if (keys.length === 0) {
                console.log('ℹ️ No data to migrate');
                await this.markMigrationCompleted();
                result.success = true;
                result.endTime = Date.now();
                return result;
            }

            // Migrate each item
            for (const key of keys) {
                try {
                    // Skip migration flag itself
                    if (key === this.migrationKey) {
                        continue;
                    }

                    const value = await this.sourceStorage.get(key);
                    if (value !== null) {
                        await this.targetStorage.set(key, value);
                        result.itemsMigrated++;

                        // Log progress every 10 items
                        if (result.itemsMigrated % 10 === 0) {
                            console.log(`Migrated ${result.itemsMigrated}/${keys.length} items`);
                        }
                    }
                } catch (error) {
                    console.error(`Failed to migrate key "${key}":`, error);
                    result.itemsFailed++;
                    result.errors.push({ key, error: error.message });
                }
            }

            // Mark migration as completed
            await this.markMigrationCompleted();

            // Optionally clear source storage
            if (clearSource && result.itemsFailed === 0) {
                console.log('🧹 Clearing source storage...');
                await this.sourceStorage.clear();
            }

            result.success = result.itemsFailed === 0;
            result.endTime = Date.now();

            const duration = ((result.endTime - result.startTime) / 1000).toFixed(2);
            console.log(`✅ Migration completed in ${duration}s`);
            console.log(`Migrated: ${result.itemsMigrated}, Failed: ${result.itemsFailed}`);

            return result;
        } catch (error) {
            console.error('❌ Migration failed:', error);
            result.errors.push({ error: error.message });
            result.endTime = Date.now();
            return result;
        }
    }

    /**
     * Verify migration by comparing source and target storage
     * @returns {Promise<Object>} Verification result
     */
    async verify() {
        console.log('Verifying migration...');

        const result = {
            success: false,
            totalItems: 0,
            matchedItems: 0,
            mismatchedItems: 0,
            mismatches: []
        };

        try {
            const sourceKeys = await this.sourceStorage.keys();
            result.totalItems = sourceKeys.length;

            for (const key of sourceKeys) {
                if (key === this.migrationKey) continue;

                const sourceValue = await this.sourceStorage.get(key);
                const targetValue = await this.targetStorage.get(key);

                if (JSON.stringify(sourceValue) === JSON.stringify(targetValue)) {
                    result.matchedItems++;
                } else {
                    result.mismatchedItems++;
                    result.mismatches.push({ key, sourceValue, targetValue });
                }
            }

            result.success = result.mismatchedItems === 0;

            console.log(`✅ Verification complete: ${result.matchedItems}/${result.totalItems} matched`);

            if (result.mismatchedItems > 0) {
                console.warn(`⚠️ Found ${result.mismatchedItems} mismatches`);
            }

            return result;
        } catch (error) {
            console.error('❌ Verification failed:', error);
            return result;
        }
    }
}

export default StorageMigration;
